import { Box, Button, DialogContent, Link, TextField, Typography } from '@mui/material';
import React from 'react';
import DialogAdaptive from '../../common/DialogAdaptive';
import StyledCardTitleBar from '../../common/StyledCardTitleBar';
import StyledDialogActions from '../../common/StyledDialogActions';

interface ElevenLabsApiKeyDialogProps {
    open: boolean;
    apiKey: string;
    onClose: () => void;
    onSave: (apiKey: string) => void;
}

const ElevenLabsApiKeyDialog: React.FC<ElevenLabsApiKeyDialogProps> = ({ open, apiKey, onClose, onSave }) => {
    const [keyInput, setKeyInput] = React.useState(apiKey || '');

    const handleSave = () => {
        onSave(keyInput.trim());
    };

    React.useEffect(() => {
        if (open) setKeyInput(apiKey || '');
    }, [open, apiKey]);

    return (
        <DialogAdaptive open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <StyledCardTitleBar title="Enter ElevenLabs API Key" />

            <DialogContent>
                <Typography gutterBottom>To use ElevenLabs' voice synthesis features, you need an API key.</Typography>

                <Box mb={2}>
                    <Typography variant="body2" gutterBottom>
                        1. Visit{' '}
                        <Link href="https://www.elevenlabs.io" target="_blank" rel="noopener noreferrer">
                            elevenlabs.io
                        </Link>{' '}
                        and sign in or create an account.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        2. Go to your{' '}
                        <Link href="https://elevenlabs.io/app/developers/api-keys" target="_blank" rel="noopener noreferrer">
                            API Keys page
                        </Link>
                        .
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        3. Click on "+ Create Key" to generate a new API key.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        4. In the settings panel, select:
                    </Typography>
                    <Box sx={{ pl: 2, mb: 1 }}>
                        <Typography variant="body2">- "Text to Speech" - Access</Typography>
                        <Typography variant="body2">- "Voices" - Read</Typography>
                        <Typography variant="body2">- "User" - Read</Typography>
                    </Box>
                    <Typography variant="body2" gutterBottom>
                        5. Copy your API Key and paste it below.
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        6. Click "Save" to apply the key.
                    </Typography>
                </Box>

                <TextField
                    fullWidth
                    label="ElevenLabs API Key"
                    variant="outlined"
                    value={keyInput}
                    onChange={e => setKeyInput(e.target.value)}
                    placeholder="e.g. 123abc456def..."
                    autoFocus
                />
            </DialogContent>

            <StyledDialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" onClick={handleSave}>
                    {keyInput.trim().length > 0 ? 'Update Key' : 'Delete Key'}
                </Button>
            </StyledDialogActions>
        </DialogAdaptive>
    );
};

export default ElevenLabsApiKeyDialog;
